import { Empty, List, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoLocale } from 'features/locale'
import { FC, useMemo } from 'react'
import styled from 'styled-components'

import {
  buildJustificationGroups,
  listJustifiedZoneOverrides,
  type NiiJustificationsConfig,
  type ZoneOverrideLogEntry,
} from './niiJustificationsApi'

const formatTimestamp = (iso: string) => dayjs(iso).format('MMM D, YYYY h:mm A')

export const NiiJustificationsPanel: FC<{
  config: NiiJustificationsConfig
  /** Where the ticket's zones live; the zones field by default. */
  zoneFieldName?: string
}> = ({ config, zoneFieldName }) => {
  const { fieldValues } = useTicketAccess()
  const { lifecycleConfig } = useWorkflow()

  const groups = useMemo(
    () =>
      buildJustificationGroups(
        fieldValues,
        config.groups,
        lifecycleConfig?.stages ?? []
      ),
    [fieldValues, config.groups, lifecycleConfig]
  )
  const overrideEntries = useMemo(
    () => listJustifiedZoneOverrides(fieldValues, zoneFieldName),
    [fieldValues, zoneFieldName]
  )

  const { formatNumber } = useGeckoLocale()
  const formatOverrideValue = ({
    field,
    justification: { overrideValue },
  }: ZoneOverrideLogEntry) =>
    field.unit && typeof overrideValue === 'number'
      ? formatNumber({
          input: overrideValue,
          inputUnit: field.unit,
          maximumFractionDigits: 2,
        })
      : (field.formatStoredValue?.(overrideValue) ?? String(overrideValue))

  if (groups.length === 0 && overrideEntries.length === 0) {
    return (
      <Empty
        description={config.emptyText}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    )
  }

  return (
    <Container>
      {groups.map((group) => (
        <StyledCard key={group.id} title={group.label}>
          <List
            dataSource={group.records}
            locale={{
              emptyText: (
                <Empty
                  description={config.emptyText}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            renderItem={(record) => (
              <List.Item>
                <RecordBody>
                  <RecordHeader>
                    <Tag>{record.action}</Tag>
                    <Typography.Text type='secondary'>
                      {record.fromStageName} → {record.toStageName}
                    </Typography.Text>
                    <Timestamp type='secondary'>
                      {formatTimestamp(record.justifiedAt)}
                    </Timestamp>
                  </RecordHeader>
                  <PreformattedText>{record.text}</PreformattedText>
                  <Typography.Text type='secondary' strong>
                    {record.justifiedBy}
                  </Typography.Text>
                </RecordBody>
              </List.Item>
            )}
          />
        </StyledCard>
      ))}
      {overrideEntries.length > 0 && (
        <StyledCard title={config.overridesTitle}>
          <List
            dataSource={[...overrideEntries]}
            renderItem={(entry) => (
              <List.Item>
                <RecordBody>
                  <RecordHeader>
                    <Tag>Override</Tag>
                    <Typography.Text strong>
                      {entry.zoneTitle} · {entry.field.label}
                    </Typography.Text>
                    <Typography.Text type='secondary'>
                      {formatOverrideValue(entry)}
                    </Typography.Text>
                    <Timestamp type='secondary'>
                      {formatTimestamp(entry.justification.justifiedAt)}
                    </Timestamp>
                  </RecordHeader>
                  <PreformattedText>
                    {entry.justification.text}
                  </PreformattedText>
                  <Typography.Text type='secondary' strong>
                    {entry.justification.justifiedBy}
                  </Typography.Text>
                </RecordBody>
              </List.Item>
            )}
          />
        </StyledCard>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

// `List.Item` lays its children out in a row; the record is a block.
const RecordBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const RecordHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const Timestamp = styled(Typography.Text)`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
`

const PreformattedText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
`
